/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CmsApi, GuidePage as GuidePageType } from "api/CmsApi";
import AppLoading from "components/AppLoading";
import { AppHeader } from "components/shared/AppHeader";
import { AppFooter } from "components/shared/AppFooter";
import { GuidePage } from "./GuidePage";
import { GuidePageIndex } from "./GuidePageIndex";
import { getUserLocale } from "utilities/UserSettingsUtilities";
import { useInlineHydration } from "hooks/useInlineHydration";
import { useLatestValue } from "hooks/useLatestValue";
import { usePrevious } from "hooks/usePrevious";

export const GuidePages: React.FC = () => {
  const { guidePageSlug } = useParams<{ guidePageSlug: string }>();
  const [guidePages, setGuidePages, wasHydrated] = useInlineHydration<
    GuidePageType[]
  >("guide-pages-root", []);
  const [isLoaded, setIsLoaded] = useState(false);

  const prevSlug = usePrevious(guidePageSlug);

  useEffect(() => {
    let isMounted = true;
    if (!guidePageSlug) {
      setIsLoaded(false);
      CmsApi.instance
        .getAllGuidePages({ locale: getUserLocale()! })
        .then((guidePages) => {
          if (isMounted) {
            setGuidePages(guidePages);
            setIsLoaded(true);
          }
        });
    } else {
      setIsLoaded(true);
    }
    return (): void => {
      isMounted = false;
    };
  }, [guidePageSlug, setGuidePages]);

  // need to let the slug state stabilize a bit because we can get kicked back
  // from a page that doesn't exist to the index, and then we need the effect above
  // to run before rendering the index, otherwise that would redirect to the
  // landing page
  if (
    (guidePageSlug === undefined && prevSlug !== undefined) ||
    (!isLoaded && !wasHydrated)
  ) {
    return <AppLoading />;
  }

  return guidePageSlug ? (
    <GuidePage slug={guidePageSlug} />
  ) : (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <AppHeader />
      <GuidePageIndex guidePages={guidePages} />
      <AppFooter />
    </div>
  );
};
